import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getFanProfile, checkFanSession } from '../services/api';
import ProfileSetup from '../components/fanzone/ProfileSetup';
import CollectionRoom from '../components/fanzone/CollectionRoom';
import FanOtpGate from '../components/fanzone/FanOtpGate';
import ComplianceWizard from '../components/fanzone/ComplianceWizard';

interface FanZonePageProps {
  userReference: string;
}

interface FanUser {
  id: number;
  name: string;
  email: string;
  status: string;
  profileImageUrl: string | null;
  userReference: string | null;
}

interface Collection {
  id: number;
  tenantId: number;
  name: string | null;
  coverUrl: string | null;
  issueNumber: number;
  productionLimit: number | null;
  productionType: string | null;
  acquiredAt: number;
}

interface Artist {
  id: number;
  artistName: string;
  slug: string;
  profileUrl: string | null;
  joinedAt: number;
  hasUncollectedPressings?: boolean;
  statusText: string | null;
  latestTransmission: { headline: string; date: number | null } | null;
}

interface FanZoneData {
  user: FanUser;
  collections: Collection[];
  artists: Artist[];
  allNotificationsMuted: boolean;
}

export default function FanZonePage({ userReference }: FanZonePageProps) {
  const [status, setStatus] = useState<'loading' | 'otp-gate' | 'profile-setup' | 'compliance' | 'dashboard' | 'editing' | 'error'>('loading');
  const [fanData, setFanData] = useState<FanZoneData | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const redirectToFanLogin = () => {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/fan-login?redirect=${redirect}`;
  };

  const loadProfile = async () => {
    try {
      const res = await getFanProfile(userReference);
      if (!res.ok) {
        if (res.status === 401) {
          redirectToFanLogin();
          return;
        }
        if (res.status === 403) {
          setErrorMsg('This My Collection link does not belong to your signed-in fan account.');
          setStatus('error');
          return;
        }
        if (res.status === 404) {
          setErrorMsg('This My Collection link is invalid or expired.');
          setStatus('error');
          return;
        }
        throw new Error('Failed to load profile');
      }

      const json = await res.json() as { data: FanZoneData };
      setFanData(json.data);

      // If user is Pending or Imported, show profile setup (no session check needed — they're activating)
      if (json.data.user.status === 'Pending' || json.data.user.status === 'Imported') {
        setStatus('profile-setup');
        return;
      }

      // User is Active — check if we have a valid session cookie
      try {
        const sessionRes = await checkFanSession();
        if (sessionRes.ok) {
          const sessionData = await sessionRes.json() as { authenticated: boolean; user?: { userReference?: string | null } };
          if (sessionData.authenticated && sessionData.user?.userReference === userReference) {
            // Valid session — show compliance wizard gate before dashboard
            setStatus('compliance');
            return;
          }
        }
      } catch {
        // Session check failed — fall through to OTP gate
      }

      // Active but no valid session — redirect to fan login and come back.
      redirectToFanLogin();
    } catch (err: any) {
      setErrorMsg(err.message || 'Something went wrong');
      setStatus('error');
    }
  };

  useEffect(() => {
    async function bootstrap() {
      try {
        const sessionRes = await checkFanSession();
        if (!sessionRes.ok) {
          redirectToFanLogin();
          return;
        }

        const sessionData = await sessionRes.json() as { authenticated: boolean; user?: { userReference?: string | null } };
        if (!sessionData.authenticated) {
          redirectToFanLogin();
          return;
        }

        if (sessionData.user?.userReference !== userReference) {
          setErrorMsg('This My Collection link does not belong to your signed-in fan account.');
          setStatus('error');
          return;
        }

        await loadProfile();
      } catch {
        redirectToFanLogin();
      }
    }

    bootstrap();
  }, [userReference]);

  const handleProfileComplete = () => {
    // Reload the My Collection data after profile setup
    setStatus('loading');
    loadProfile();
  };

  const handleOtpAuthenticated = () => {
    // OTP verify succeeded — reload the protected profile for this user reference.
    setStatus('loading');
    loadProfile();
  };

  const handleComplianceComplete = () => {
    // Compliance wizard done (or queue was empty) — go to dashboard
    setStatus('dashboard');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
          <p className="text-xs text-text-muted font-mono">loading_my_collection...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-deep-charcoal flex flex-col items-center justify-center px-4">
        <img src="/fans-audio-logo.png" alt="fans.audio" className="h-10 mb-6 opacity-50" />
        <h1 className="text-xl font-bold text-white mb-2">My Collection Not Found</h1>
        <p className="text-sm text-text-secondary font-mono text-center max-w-sm">{errorMsg}</p>
      </div>
    );
  }

  if (status === 'otp-gate') {
    return (
      <FanOtpGate
        onAuthenticated={handleOtpAuthenticated}
      />
    );
  }

  if (status === 'profile-setup' && fanData) {
    return (
      <ProfileSetup
        userReference={userReference}
        currentName={fanData.user.name}
        onComplete={handleProfileComplete}
      />
    );
  }

  if (status === 'compliance') {
    return (
      <ComplianceWizard
        userReference={userReference}
        onComplete={handleComplianceComplete}
      />
    );
  }

  if (status === 'editing' && fanData) {
    return (
      <ProfileSetup
        userReference={userReference}
        currentName={fanData.user.name}
        currentImageUrl={fanData.user.profileImageUrl}
        mode="edit"
        onComplete={handleProfileComplete}
        onCancel={() => setStatus('dashboard')}
      />
    );
  }

  if (status === 'dashboard' && fanData) {
    return (
      <CollectionRoom
        user={fanData.user}
        collections={fanData.collections}
        artists={fanData.artists}
        allNotificationsMuted={fanData.allNotificationsMuted}
        userReference={userReference}
        onEditProfile={() => setStatus('editing')}
      />
    );
  }

  return null;
}
